import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import {
  GetChangeRequestAttachmentDto,
  GetWorkPackageAttachmentDto,
  GetWorkPackageDto,
} from '@codefluegel/zeta-change-typescript-client';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Divider,
  Link,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import DescriptionIcon from '@mui/icons-material/Description';
import { useChangeStore } from '../../store/changeStore';
import { useChangeApiFactory, useWorkPackageApiFactory } from '../../ApiClient';
import useWorkPackageName from '../../hooks/useWorkPackageName';
import { getDepartmentById } from '../../utils/utils';

// Make AttachmentList generic for type safety
type AttachmentListProps<T> = {
  attachments: T[];
  downloadFile: (item: T) => void;
};

function AttachmentList<
  T extends { id: number | undefined; fileName?: string | null },
>({ attachments, downloadFile }: AttachmentListProps<T>) {
  return (
    <List>
      {attachments.map(attachment => (
        <ListItem
          onClick={() => downloadFile(attachment)}
          key={attachment.id}
          sx={{
            alignItems: 'flex-start',
            cursor: 'pointer',
            paddingLeft: theme => theme.spacing(2),
            '&:hover': {
              backgroundColor: theme => theme.palette.action.hover,
            },
          }}
        >
          <DescriptionIcon sx={{ mr: 1, color: 'text.secondary' }} />
          <ListItemText primary={attachment.fileName} />
        </ListItem>
      ))}
    </List>
  );
}

const ChangeAttachmentsPage = observer(() => {
  const { change, isDataLoading, reducedWorkPackages } = useChangeStore();
  const changeApi = useChangeApiFactory();
  const workPackageApi = useWorkPackageApiFactory();

  const { t } = useTranslation();

  const saveData = (buffer: any, name: string) => {
    const url = window.URL.createObjectURL(new Blob([buffer]));
    const link = document.createElement('a');
    link.href = url;
    link.download = name;
    link.click();
  };

  const downloadFile = async (
    item: GetChangeRequestAttachmentDto | GetWorkPackageAttachmentDto,
  ) => {
    if (change?.id) {
      const file = await changeApi.changesControllerDownloadAttachment(
        { id: change.id, attachmentId: item.id },
        {
          responseType: 'arraybuffer',
        },
      );
      saveData(file.data, item.fileName || '');
    }
  };

  const calcAttachments =
    change?.attachments?.filter(attachment => attachment.isCalc) || [];
  const nonCalcAttachments =
    change?.attachments?.filter(attachment => !attachment.isCalc) || [];

  const [workPackagesWithAttachments, setWorkPackagesWithAttachments] =
    useState<GetWorkPackageDto[]>([]);

  useEffect(() => {
    const fetchAttachments = async () => {
      if (!change?.processes || !reducedWorkPackages) {
        return;
      }

      const results = await Promise.all(
        change.processes
          .flatMap(process => reducedWorkPackages[process.id] || [])
          .filter(wp => wp.hasAttachments)
          .map(async wp => {
            try {
              const fetchedWorkPackage =
                await workPackageApi.workPackagesControllerFindOne({
                  id: wp.id,
                });
              return fetchedWorkPackage.data;
            } catch (error) {
              console.error(
                `Failed to fetch work package with ID ${wp.id}:`,
                error,
              );
              return null;
            }
          }),
      );

      setWorkPackagesWithAttachments(
        results.filter((wp): wp is GetWorkPackageDto => wp !== null),
      );
    };

    fetchAttachments();
  }, [change, reducedWorkPackages, workPackageApi]);

  if (isDataLoading) {
    return <Typography>Loading attachments...</Typography>;
  }

  function WorkPackageRow({ wp }: { wp: GetWorkPackageDto }) {
    const wpName = useWorkPackageName(
      wp as GetWorkPackageDto & { hasAttachments: boolean },
    );

    const process = change?.processes.find(p => p.id === wp.processId);
    const department = getDepartmentById(
      process?.pId ?? 0,
      change?.commonProject?.navProcessToCommonProjectRelations,
    );

    return (
      <Box mb={2}>
        {wpName && (
          <Box mb={1}>
            <Typography
              variant="h5"
              sx={{ fontSize: '0.875rem' }}
              component="span"
            >
              {department?.name}
            </Typography>
            <Typography
              variant="body2"
              component="span"
              sx={{ ml: 1, color: 'text.secondary' }}
            >
              {wpName || 'Loading...'}
            </Typography>
          </Box>
        )}

        <AttachmentList
          attachments={wp.attachments}
          downloadFile={downloadFile}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Non-Calculation Documents Section */}
      <Box mb={4}>
        <Typography variant="h4" gutterBottom>
          {t('documents.attachments')}
        </Typography>
        {nonCalcAttachments.length > 0 ? (
          <AttachmentList<GetChangeRequestAttachmentDto>
            downloadFile={(item: GetChangeRequestAttachmentDto) =>
              downloadFile(item)
            }
            attachments={nonCalcAttachments}
          />
        ) : (
          <Typography variant="body2" color="text.secondary">
            {t('documents.noAttachments')}
          </Typography>
        )}
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Calculation Documents Section */}
      <Box mb={4}>
        <Typography variant="h4" gutterBottom>
          {t('documents.calcDocs')}
        </Typography>
        {calcAttachments.length > 0 ? (
          <AttachmentList<GetChangeRequestAttachmentDto>
            downloadFile={(item: GetChangeRequestAttachmentDto) =>
              downloadFile(item)
            }
            attachments={calcAttachments}
          />
        ) : (
          <Typography variant="body2" color="text.secondary">
            {t('documents.noAttachments')}
          </Typography>
        )}
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Empty Third Section */}
      <Box>
        <Typography variant="h4" gutterBottom>
          {t('workPackage.workPackages')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {' '}
        </Typography>

        {workPackagesWithAttachments.map(wp => (
          <WorkPackageRow key={wp.id} wp={wp} />
        ))}
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* SharePoint Link Section */}
      {change?.sharepointLink && (
        <Box mb={4} sx={{ width: '90%' }}>
          <Typography variant="h4" gutterBottom>
            SharePoint Folder
          </Typography>
          <ListItem
            component={Link}
            href={change?.sharepointLink}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              textDecoration: 'none',
              color: 'inherit',
              paddingLeft: theme => theme.spacing(2),
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <FolderOpenIcon sx={{ mr: 1, color: 'text.secondary' }} />
            <ListItemText primary={t('documents.openSharepoint')} />
          </ListItem>
        </Box>
      )}
    </Box>
  );
});

export default ChangeAttachmentsPage;
